import React, { useEffect, useMemo, useState } from "react";

const SCHOOL_HEADER_THEME = {
  "school-of-management": "white",
  "school-of-commerce": "white",
  "school-of-engineering-and-technology": "white",
  "school-of-information-and-technology": "white",
  "school-of-hotel-management": "blue",
  "school-of-science": "white",
  "school-of-arts-and-humanities": "blue",
  "school-of-law": "white",
  "school-of-pharmacy": "blue",
  "school-of-design": "burgundy",
  "school-of-education": "burgundy",
  "school-of-journalism-and-mass-communication": "white",
  "school-of-media-and-entertainment": "white",
  "school-of-library-and-information-science": "blue",
  "school-of-yoga-and-naturopathy": "blue",
  "school-of-life-science": "white",
  "school-of-vocational-management": "blue",
  "school-of-health-and-allied-science": "burgundy",
};

function ProgrammeGroup({ group, className = "", hoveredSchool, setHoveredSchool }) {
  if (!group) return null;

  return (
    <div className={`mega-group ${className}`.trim()}>
      <h3>{group.title}</h3>

      {group.links?.map((school) => (
        <div
          key={school.url || school.title}
          className="school-item"
          onMouseEnter={() => setHoveredSchool(school)}
          onMouseLeave={() => setHoveredSchool(null)}
        >
          <a href={school.url || "#"}>
            {school.title}
            {school.courses?.length > 0 && <span>›</span>}
          </a>

          {hoveredSchool?.url === school.url && school.courses?.length > 0 && (
            <div className="course-dropdown">
              {school.courses.map((course) => (
                <a key={course.url || course.title} href={course.url || "#"}>
                  {course.title}
                </a>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

function MobileGroup({ group, closeMenu }) {
  if (!group) return null;

  return (
    <div className="mobile-sub-group">
      <h6>{group.title}</h6>

      {group.links?.map((link) => (
        <React.Fragment key={link.url || link.title}>
          <a href={link.url || "#"} onClick={closeMenu}>
            {link.title}
          </a>

          {link.courses?.map((course) => (
            <a
              className="mobile-course-link"
              href={course.url || "#"}
              key={course.url || course.title}
              onClick={closeMenu}
            >
              {course.title}
            </a>
          ))}
        </React.Fragment>
      ))}
    </div>
  );
}

function UniversityHeader({
  theme = "white",
  menuData = {},
  utilityLinks = [],
  logo,
  logoWhite,
}) {
  const [menu, setMenu] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileSection, setMobileSection] = useState(null);
  const [scrolled, setScrolled] = useState(false);
  const [hoveredSchool, setHoveredSchool] = useState(null);

  const menuEntries = useMemo(() => Object.entries(menuData || {}), [menuData]);
  const activeMenu = menu ? menuData?.[menu] : null;
  const isBlue = theme === "blue";
  const isBurgundy = theme === "burgundy";
  const isDark = isBlue || isBurgundy;

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = mobileOpen ? "hidden" : previousOverflow;

    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [mobileOpen]);

  useEffect(() => {
    setHoveredSchool(null);
  }, [menu]);

  const closeMobileMenu = () => {
    setMobileOpen(false);
    setMobileSection(null);
  };

  return (
    <>
      <header
        className={`${isDark ? "site-header-blue" : "site-header-inner"} ${isBurgundy ? "site-header-burgundy" : ""} ${scrolled ? "is-scrolled" : ""}`}
        style={isBurgundy ? { backgroundColor: "#8B1E3F" } : undefined}
        onMouseLeave={() => setMenu(null)}
      >
        <div className="nav-shell">
          <a className="brand" href="/" aria-label="ISBM University home">
            <img
              src={isDark ? logoWhite || logo : logo}
              className="brand-logo"
              alt="ISBM University"
            />
          </a>

          <div className="nav-stack">
            <div className="utility-bar">
              <nav aria-label="Utility navigation">
                {utilityLinks.map((link) => (
                  <a key={link.url || link.title} href={link.url || "#"}>
                    {link.title}
                  </a>
                ))}
              </nav>

              <div className="utility-actions">
                <a href="tel:+919373199999" className="phone">
                  <span>Call us</span> +91 93731 99999
                </a>

                <a href="/apply" className="utility-apply">
                  Apply Online <span>↗</span>
                </a>
              </div>
            </div>

            <nav className="primary-nav" aria-label="Primary navigation">
              {menuEntries.map(([item]) => (
                <button
                  key={item}
                  type="button"
                  className={menu === item ? "active" : ""}
                  onMouseEnter={() => setMenu(item)}
                  onFocus={() => setMenu(item)}
                  aria-expanded={menu === item}
                >
                  {item}
                  <span><i className="ti ti-chevrons-down" /></span>
                </button>
              ))}

              <button type="button" className="search-btn" aria-label="Search">
                <svg viewBox="0 0 24 24" fill="none" aria-hidden="true">
                  <circle cx="11" cy="11" r="6.5" />
                  <path d="m16 16 4 4" />
                </svg>
              </button>
            </nav>
          </div>

          <div className="mobile-actions">
            <a href="/apply" className="mini-apply">Apply</a>

            <button
              type="button"
              className={`menu-toggle ${mobileOpen ? "open" : ""}`}
              onClick={() => setMobileOpen((open) => !open)}
              aria-expanded={mobileOpen}
              aria-controls="mobile-navigation-panel"
              aria-label="Toggle menu"
            >
              <span />
              <span />
              <span />
            </button>
          </div>
        </div>

        <div
          className={`mega-menu ${activeMenu ? "open" : ""}`}
          role="region"
          aria-label="Navigation submenu"
        >
          {activeMenu && (
            <div className="mega-inner" key={menu}>
              <div className="mega-intro">
                <span className="mega-eyebrow">{activeMenu.eyebrow}</span>
                <h2>{activeMenu.title}</h2>
                <p>{activeMenu.description}</p>
                <a href={activeMenu.url || "#"}>Overview <span>→</span></a>
              </div>

              {menu === "Programmes" ? (
                <>
                  <ProgrammeGroup
                    group={activeMenu.groups?.[0]}
                    className="mega-relative"
                    hoveredSchool={hoveredSchool}
                    setHoveredSchool={setHoveredSchool}
                  />
                  <ProgrammeGroup
                    group={activeMenu.groups?.[1]}
                    className="mega-relative-2"
                    hoveredSchool={hoveredSchool}
                    setHoveredSchool={setHoveredSchool}
                  />
                </>
              ) : (
                activeMenu.groups?.map((group) => (
                  <div className="mega-group" key={group.title}>
                    <h3>{group.title}</h3>
                    {group.links?.map((link) => (
                      <a key={link.url || link.title} href={link.url || "#"}>
                        {link.title}
                      </a>
                    ))}
                  </div>
                ))
              )}

              <a href={activeMenu.url || "#"} className="mega-feature">
                {activeMenu.image && <img src={activeMenu.image} alt="" />}
                <span>
                  <small>Featured</small>
                  <strong>Experience our university</strong>
                  <i>Explore now →</i>
                </span>
              </a>
            </div>
          )}
        </div>
      </header>

      <div
        id="mobile-navigation-panel"
        className={`mobile-panel ${mobileOpen ? "open" : ""}`}
        aria-hidden={!mobileOpen}
      >
        <div className="mobile-utility">
          {utilityLinks.map((link) => (
            <a key={link.url || link.title} href={link.url || "#"} onClick={closeMobileMenu}>
              {link.title}
            </a>
          ))}
          <a href="tel:+919373199999">Call Us</a>
        </div>

        <nav aria-label="Mobile navigation">
          {menuEntries.map(([item, data]) => (
            <div className="mobile-menu-item" key={item}>
              <button
                type="button"
                onClick={() => setMobileSection((section) => section === item ? null : item)}
                aria-expanded={mobileSection === item}
              >
                {item}<span>{mobileSection === item ? "−" : "+"}</span>
              </button>

              <div className={`mobile-sub ${mobileSection === item ? "open" : ""}`}>
                {data.groups?.map((group) => (
                  <MobileGroup key={group.title} group={group} closeMenu={closeMobileMenu} />
                ))}

                <a href={data.url || "#"} className="mobile-overview" onClick={closeMobileMenu}>
                  View {item} →
                </a>
              </div>
            </div>
          ))}
        </nav>

        <a className="mobile-cta" href="/apply" onClick={closeMobileMenu}>
          Begin your application <span>→</span>
        </a>
      </div>
    </>
  );
}

export function HeaderWhite(props) {
  return <UniversityHeader {...props} theme="white" />;
}

export function HeaderBlue(props) {
  return <UniversityHeader {...props} theme="blue" />;
}

export function HeaderBurgundy(props) {
  return <UniversityHeader {...props} theme="burgundy" />;
}

export default function Header({
  school,
  schoolDetails,
  menuData,
  utilityLinks,
  logo,
  logoWhite,
  theme: themeOverride,
}) {
  const schoolObject = typeof school === "object" && school !== null ? school : schoolDetails;
  const schoolId = typeof school === "string" ? school : schoolObject?.id || schoolObject?.slug;
  const theme = themeOverride || SCHOOL_HEADER_THEME[schoolId];

  if (!theme) return null;

  return (
    <UniversityHeader
      theme={theme}
      menuData={menuData}
      utilityLinks={utilityLinks}
      logo={logo}
      logoWhite={logoWhite}
    />
  );
}

export { SCHOOL_HEADER_THEME };
